import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, CheckCircle2, Clock, Sparkles, AlertCircle } from "lucide-react";

export default function TreatmentModal({ treatment, open, onClose }) {
  if (!treatment) return null;
  const t = treatment;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl bg-[#F4EFE4] p-0 overflow-hidden border-[#E8DFD2] max-h-[90vh]"
        data-testid="treatment-modal"
      >
        <ScrollArea className="max-h-[90vh]">
          {/* Hero image */}
          <div className="relative h-56 sm:h-64 overflow-hidden">
            <img src={t.image} alt={t.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3A2418]/80 via-[#3A2418]/20 to-transparent"></div>
            <div className="absolute bottom-5 left-6 right-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 bg-white/95 text-[#3A2418] text-xs font-medium px-3 py-1 rounded-full">
                  <Clock className="w-3 h-3 text-[#B5704A]" />
                  {t.duration}
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-[400] text-white leading-tight" data-testid="modal-treatment-title">
                {t.title}
              </h2>
              {t.tagline && (
                <p className="font-display italic text-[#FAE0CE] text-base sm:text-lg mt-1">{t.tagline}</p>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8 space-y-6">
            <p className="text-[#3A2418] leading-relaxed">{t.description}</p>

            <div className="grid sm:grid-cols-2 gap-4">
              {t.idealFor && (
                <div className="bg-white rounded-xl p-5 border border-[#E8DFD2]">
                  <span className="label-eyebrow text-[#B5704A] text-[10px]">Ideal para</span>
                  <ul className="mt-3 space-y-2">
                    {t.idealFor.map((i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3A2418]">
                        <Sparkles className="w-3.5 h-3.5 text-[#B5704A] mt-1 shrink-0" />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {t.helpsWith && (
                <div className="bg-white rounded-xl p-5 border border-[#E8DFD2]">
                  <span className="label-eyebrow text-[#B5704A] text-[10px]">Ayuda a mejorar</span>
                  <ul className="mt-3 space-y-2">
                    {t.helpsWith.map((i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3A2418]">
                        <Sparkles className="w-3.5 h-3.5 text-[#B5704A] mt-1 shrink-0" />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {t.benefits && t.benefits.length > 0 && (
                <div className="bg-white rounded-xl p-5 border border-[#E8DFD2]">
                  <span className="label-eyebrow text-[#B5704A] text-[10px]">✨ Beneficios</span>
                  <ul className="mt-3 space-y-2">
                    {t.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[#3A2418]">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#B5704A] mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {t.result && (
                <div className="bg-[#3A2418] rounded-xl p-5 text-[#FAE0CE] border border-[#3A2418]">
                  <span className="label-eyebrow text-[#E8B79A] text-[10px]">✨ Resultado</span>
                  <p className="font-display text-lg italic font-[400] mt-3 leading-snug">{t.result}</p>
                </div>
              )}
            </div>

            {t.note && (
              <div className="flex items-start gap-3 bg-[#FDF3EF] border-l-4 border-l-[#D87D65] rounded-r-md p-4">
                <AlertCircle className="w-4 h-4 text-[#D87D65] mt-0.5 shrink-0" />
                <p className="text-sm text-[#3A2418]">{t.note}</p>
              </div>
            )}

            <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Link
                to={`/reservar?servicio=${t.slug}`}
                onClick={onClose}
                className="flex-1"
                data-testid="modal-book-cta"
              >
                <Button size="lg" className="w-full bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full h-12">
                  Agendar este tratamiento <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button variant="ghost" onClick={onClose} className="rounded-full text-[#6F5A4C]" data-testid="modal-close-btn">
                Cerrar
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
